import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { MY_PURCHASES_V3 } from 'my-purchases/constants/endpoints'
import {
	PurchasesStateContext,
	PurchasesStateDispatchContext,
} from 'my-purchases/context/PurchasesProvider'
import { useFetch } from 'my-purchases/hooks/useFetch/useFetch'
import type { PurchaseResponse } from 'my-purchases/types/purchaseList'
import { useContext, useEffect } from 'react'
import { useSession } from 'session/src/hooks/useSession'
import useSWRImmutable from 'swr/immutable'
import { StatusCode } from 'types/statusCodes'
import { url } from 'utils/url'
import { normalizeData } from './normalization'

interface PurchasesListResponse {
	purchases: PurchaseResponse[]
	endCursor: string
	hasNextPage: boolean
}

const PURCHASES_PER_PAGE = 10

export const usePurchases = () => {
	const { fetcher } = useFetch()
	const { isReady, logged } = useSession()
	const { channel } = useMasterData()
	const { purchases, cursor } = useContext(PurchasesStateContext)
	const { dispatch } = useContext(PurchasesStateDispatchContext)

	const fetchAfterItem = cursor ? `&after=${cursor}` : ''

	const { data, error, isLoading, isValidating, mutate } = useSWRImmutable<{
		resolveData: PurchasesListResponse
		status: number
	} | null>(
		isReady && logged
			? {
					url: url(
						`${MY_PURCHASES_V3}/purchases?channelId=${channel}&first=${PURCHASES_PER_PAGE}${fetchAfterItem}`
					),
				}
			: null,
		fetcher,
		{}
	)

	const { resolveData, status } = data ?? {}
	const statusErrorCode =
		status === StatusCode.MULTI_STATUS ? status : error?.status

	useEffect(() => {
		if (resolveData && resolveData.purchases?.length > 0) {
			const newPurchases = normalizeData({ purchases: resolveData.purchases })

			// Filter to not include repeated purchases
			const existingIds = new Set(purchases.map((p) => p.purchaseId))
			const filteredPurchases = newPurchases.filter(
				(p) => !existingIds.has(p.purchaseId)
			)
			if (filteredPurchases.length > 0) {
				dispatch({
					purchases: [...purchases, ...filteredPurchases],
				})
			}
		}
	}, [resolveData, dispatch])

	const loadMore = () => {
		if (resolveData?.hasNextPage) {
			dispatch({ cursor: resolveData.endCursor })
		}
	}

	return {
		purchases,
		loadMore,
		reload: mutate,
		error: statusErrorCode,
		isError: !!statusErrorCode,
		isLoading: !isReady || isLoading || isValidating,
		showLoadMoreButton: !statusErrorCode && resolveData?.hasNextPage,
		hasNextPage: !!resolveData?.hasNextPage,
	}
}
